import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDraggable,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Copy, Save, Send, X, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { BlockView } from "./BlockRenderer";
import { PALETTE, SECTION_PRESETS, presetBlocks, newBlock, starterBlocks, uid } from "@/lib/blocks";
import "./pagebuilder.css";

/* One sortable block on the canvas, wrapped with a hover toolbar. */
function SortableBlock({ block, selected, onSelect, onChange, onDelete, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(block.id)}
      className={`relative group my-2 rounded-lg border-2 transition-colors ${
        selected ? "border-[var(--dojo-green)]" : "border-transparent hover:border-[var(--dojo-border)]"
      }`}
    >
      <div className="absolute -top-3 right-3 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing bg-[var(--dojo-maroon)] text-white p-1.5 rounded shadow"
          title="Drag to reorder"
        >
          <GripVertical size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(block.id); }}
          className="bg-[var(--dojo-card)] border border-[var(--dojo-border)] text-[var(--dojo-ink)] p-1.5 rounded shadow"
          title="Duplicate block"
        >
          <Copy size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
          className="bg-[var(--dojo-card)] border border-[var(--dojo-border)] text-[var(--dojo-hinomaru)] p-1.5 rounded shadow"
          title="Delete block"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className="absolute -top-3 left-3 z-10 text-[10px] uppercase tracking-wider bg-[var(--dojo-ink)] text-[var(--dojo-paper)] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition">
        {block.type}
      </div>
      <BlockView block={block} edit onChange={(np) => onChange(block.id, np)} />
    </div>
  );
}

/* Draggable palette item. Click to append, or drag onto the canvas. */
function PaletteItem({ item, onAdd }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${item.type}`,
  });
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      type="button"
      onClick={() => onAdd(item.type)}
      className={`w-full text-left px-3 py-2 rounded-lg border bg-[var(--dojo-card)] hover:border-[var(--dojo-maroon)] cursor-grab active:cursor-grabbing transition ${
        isDragging ? "opacity-40 border-[var(--dojo-maroon)]" : "border-[var(--dojo-border)]"
      }`}
    >
      <div className="text-sm font-medium text-[var(--dojo-ink)]">{item.label}</div>
      <div className="text-[11px] text-[var(--dojo-ink-soft)]">{item.desc}</div>
    </button>
  );
}

/* Drop zone at the end of the canvas so blocks can be dropped into empty space. */
function CanvasEnd() {
  const { setNodeRef, isOver } = useDroppable({ id: "canvas-end" });
  return (
    <div
      ref={setNodeRef}
      className={`mt-4 rounded-lg border-2 border-dashed text-center text-sm py-8 transition ${
        isOver
          ? "border-[var(--dojo-green)] bg-[var(--dojo-green-soft)] text-[var(--dojo-green-dark)]"
          : "border-[var(--dojo-border)] text-[var(--dojo-ink-soft)]"
      }`}
    >
      Drop a block here, or click a block in the palette to add it
    </div>
  );
}

export default function PageBuilder({ slug, page, lang = "en", onClose, onSaved }) {
  // Which language copy we're editing. Spanish edits are stored under
  // `content.i18n.es` so the English source is never overwritten.
  const [editLang, setEditLang] = useState(lang === "es" ? "es" : "en");

  const target = useMemo(() => {
    const base = page?.content || {};
    if (editLang !== "es") return base;
    const es = base.i18n?.es;
    return es && typeof es === "object" ? es : base;
  }, [page, editLang]);

  const hasSpanishCopy = !!page?.content?.i18n?.es;

  const initial = useMemo(() => {
    const draft = target?.draft;
    if (Array.isArray(draft) && draft.length) return draft.map((x) => ({ ...x, id: x.id || uid() }));
    const b = target?.blocks;
    return Array.isArray(b) && b.length ? b.map((x) => ({ ...x, id: x.id || uid() })) : starterBlocks(slug, target);
  }, [slug, target]);

  const startedFromDraft = Array.isArray(target?.draft) && target.draft.length > 0;
  const isPublished = Array.isArray(target?.blocks) && target.blocks.length > 0;

  const [blocks, setBlocks] = useState(initial);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const [activeType, setActiveType] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const ids = blocks.map((b) => b.id);

  const updateBlock = (id, np) => setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, props: np } : b)));
  const deleteBlock = (id) => setBlocks((bs) => bs.filter((b) => b.id !== id));
  const duplicateBlock = (id) =>
    setBlocks((bs) => {
      const idx = bs.findIndex((b) => b.id === id);
      if (idx < 0) return bs;
      const copy = { ...bs[idx], id: uid(), props: { ...bs[idx].props } };
      const next = bs.slice();
      next.splice(idx + 1, 0, copy);
      return next;
    });
  const addBlock = (type, index = null) =>
    setBlocks((bs) => {
      const nb = newBlock(type);
      if (index == null || index >= bs.length) return [...bs, nb];
      const next = bs.slice();
      next.splice(index, 0, nb);
      return next;
    });
  const addPreset = (key) => setBlocks((bs) => [...bs, ...presetBlocks(key)]);

  // Re-seed the canvas when the admin flips between the English and Spanish copy.
  useEffect(() => {
    setBlocks(initial);
    setSelected(null);
    setStatus("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLang]);

  const switchLang = (next) => {
    if (next === editLang) return;
    if (!window.confirm(next === "es"
      ? "Switch to editing the Spanish copy? Unsaved changes to the English page are discarded."
      : "Switch back to the English page? Unsaved changes to the Spanish copy are discarded.")) return;
    setEditLang(next);
  };

  // Re-import the page's original hand-built content, discarding block edits.
  const resetToOriginal = () => {
    if (!window.confirm("Reset this page back to its original design? Any blocks you added or edited here will be discarded (nothing is saved until you click Save draft or Publish).")) return;
    setBlocks(starterBlocks(slug, target));
    setSelected(null);
    setStatus("Reset to original ✓");
    setTimeout(() => setStatus(""), 2500);
  };

  const onDragStart = (e) => {
    const a = String(e.active.id);
    if (a.startsWith("palette:")) setActiveType(a.slice(8));
  };

  const onDragEnd = (e) => {
    const active = e.active;
    const over = e.over;
    setActiveType(null);
    if (!over) return;
    const aid = String(active.id);
    if (aid.startsWith("palette:")) {
      const type = aid.slice(8);
      const overId = String(over.id);
      const oi = blocks.findIndex((b) => b.id === overId);
      addBlock(type, oi >= 0 ? oi : null);
      return;
    }
    if (active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id);
      const newIndex = ids.indexOf(over.id);
      if (oldIndex >= 0 && newIndex >= 0) setBlocks((bs) => arrayMove(bs, oldIndex, newIndex));
    }
  };

  const persist = async (mode) => {
    setSaving(true);
    setErr("");
    setStatus("");
    try {
      const base = page?.content || {};
      let content;
      if (editLang === "es") {
        // Seed the Spanish copy from the English page the first time.
        const esBase = { ...(base.i18n?.es || base) };
        delete esBase.i18n;
        const es = mode === "publish"
          ? { ...esBase, blocks, draft: null }
          : { ...esBase, draft: blocks };
        content = { ...base, i18n: { ...(base.i18n || {}), es } };
      } else {
        content =
          mode === "publish"
            ? { ...base, blocks, draft: null }
            : { ...base, draft: blocks };
      }
      const { data } = await api.put(`/cms/pages/${slug}`, {
        title: page?.title || slug,
        content,
      });
      if (mode === "publish") {
        onSaved?.(data, "publish");
      } else {
        // Keep the builder open so the admin can keep editing / previewing.
        setStatus("Draft saved ✓");
        setTimeout(() => setStatus(""), 2500);
      }
    } catch (e) {
      setErr(formatApiError ? formatApiError(e) : "Save failed. Check permissions and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[var(--dojo-paper)] flex flex-col">
      {/* Toolbar */}
      <div className="shrink-0 border-b border-[var(--dojo-border)] bg-[var(--dojo-card)] px-4 py-3 flex items-center gap-3">
        <div className="font-serif text-lg text-[var(--dojo-ink)]">
          Editing <span className="text-[var(--dojo-maroon)]">/{slug}</span>
        </div>
        <div className="flex items-center border border-[var(--dojo-border)] rounded overflow-hidden shrink-0" data-testid="pb-lang-switch">
          {[["en", "English"], ["es", "Español"]].map(([code, label]) => (
            <button
              key={code}
              onClick={() => switchLang(code)}
              data-testid={`pb-lang-${code}`}
              className={`px-2.5 py-1 text-[11px] uppercase tracking-wider transition ${
                editLang === code
                  ? "bg-[var(--dojo-maroon)] text-white"
                  : "text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]"
              }`}
              title={code === "es" ? "Review and fix the Spanish copy" : "Edit the English page"}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="text-xs text-[var(--dojo-ink-soft)] hidden md:flex items-center gap-2">
          <span>Click text to edit · drag to reorder · add from the left</span>
          {!isPublished && (
            <span className="px-2 py-0.5 rounded-full bg-[var(--dojo-maroon-soft)] text-[var(--dojo-maroon)] text-[10px] uppercase tracking-wider">Not published</span>
          )}
          {startedFromDraft && (
            <span className="px-2 py-0.5 rounded-full bg-[var(--dojo-green-soft)] text-[var(--dojo-green-dark)] text-[10px] uppercase tracking-wider">Editing draft</span>
          )}
          {editLang === "es" && !hasSpanishCopy && (
            <span className="px-2 py-0.5 rounded-full bg-[var(--dojo-maroon-soft)] text-[var(--dojo-maroon)] text-[10px] uppercase tracking-wider">No Spanish copy yet — translate it from Live Preview first</span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {status && <span className="text-xs text-[var(--dojo-green-dark)]">{status}</span>}
          {err && <span className="text-xs text-[var(--dojo-hinomaru)] max-w-[220px] truncate">{err}</span>}
          <button
            onClick={resetToOriginal}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-2 rounded border border-[var(--dojo-border)] text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)] text-sm"
            data-testid="pb-reset-original"
            title="Re-import the original page content"
          >
            <RotateCcw size={15} /> Reset to original
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 px-3 py-2 rounded border border-[var(--dojo-border)] text-[var(--dojo-ink)] text-sm"
            data-testid="pb-cancel"
          >
            <X size={15} /> Cancel
          </button>
          <button
            onClick={() => persist("draft")}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-2 rounded border border-[var(--dojo-maroon)] text-[var(--dojo-maroon)] text-sm"
            data-testid="pb-save-draft"
          >
            <Save size={15} /> {saving ? "…" : "Save draft"}
          </button>
          <button
            onClick={() => persist("publish")}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-1 text-sm"
            data-testid="pb-publish"
          >
            <Send size={15} /> {saving ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <div className="flex-1 min-h-0 flex">
          {/* Palette */}
          <aside className="w-56 shrink-0 border-r border-[var(--dojo-border)] bg-[var(--dojo-paper-alt)] p-3 overflow-y-auto">
            <div className="text-[11px] uppercase tracking-wider text-[var(--dojo-ink-soft)] mb-2">
              Sections <span className="normal-case tracking-normal opacity-70">· 1-click</span>
            </div>
            <div className="space-y-2 mb-5">
              {SECTION_PRESETS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => addPreset(s.key)}
                  data-testid={`preset-${s.key}`}
                  className="w-full text-left px-3 py-2 rounded-lg border border-[var(--dojo-border)] bg-[var(--dojo-card)] hover:border-[var(--dojo-green)] transition"
                >
                  <div className="text-sm font-medium text-[var(--dojo-ink)]">{s.label}</div>
                  <div className="text-[11px] text-[var(--dojo-ink-soft)]">{s.desc}</div>
                </button>
              ))}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--dojo-ink-soft)] mb-2">
              Blocks
            </div>
            <div className="space-y-2">
              {PALETTE.map((item) => (
                <PaletteItem key={item.type} item={item} onAdd={(t) => addBlock(t)} />
              ))}
            </div>
          </aside>

          {/* Canvas */}
          <main className="flex-1 min-w-0 overflow-y-auto bg-[var(--dojo-paper)]" data-testid="pb-canvas">
            <div className="max-w-5xl mx-auto py-6 px-3">
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {blocks.map((b) => (
                  <SortableBlock
                    key={b.id}
                    block={b}
                    selected={selected === b.id}
                    onSelect={setSelected}
                    onChange={updateBlock}
                    onDelete={deleteBlock}
                    onDuplicate={duplicateBlock}
                  />
                ))}
              </SortableContext>
              <CanvasEnd />
            </div>
          </main>
        </div>

        <DragOverlay>
          {activeType ? (
            <div className="px-3 py-2 rounded-lg bg-[var(--dojo-maroon)] text-white text-sm shadow-lg">
              + {activeType}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
